/**
 * PlatformAdminView — the platform owner's control surface.
 *
 * This is the owner-only home (organizers never see it; the router gates it on
 * the "owner" role). Its first capability is curating the FEATURED league shown
 * publicly. The rule is "at most one featured league per tenant", so the owner
 * picks a league from the cross-tenant dropdown to feature it, and can clear any
 * currently-featured league from the list.
 *
 * It is intentionally built as a shell with room to grow: the "Reports &
 * Insights" section is a placeholder for the forthcoming global analytics.
 */

import { apiService } from "../services/api";
import { renderActivityTrendCard } from "../components/activityTrendChart";
import { ConfirmationModal } from "../components/ConfirmationModal";
import { showNotification } from "../utils/ui";
import type { OwnerLeague, Organizer, OrganizerCredentials, AuditEntry, OrganizerLead, LeadCredentials } from "../types/index";
import type { PlatformAnalytics, WeekdayActivity } from "shared";

export class PlatformAdminView {
  private container: HTMLElement;
  private leagues: OwnerLeague[] = [];
  private organizers: Organizer[] = [];
  private leads: OrganizerLead[] = [];
  private auditLog: AuditEntry[] = [];
  private analytics: PlatformAnalytics | null = null;
  private error: string | null = null;

  constructor(container: HTMLElement) {
    this.container = container;
  }

  async render(): Promise<void> {
    this.container.innerHTML = this.renderShell(`
      <div class="flex items-center justify-center gap-3 py-24 text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">
        <span class="w-5 h-5 rounded-full border-2 border-primary/25 border-t-primary daylight:border-day-ink/15 daylight:border-t-day-ink animate-spin" aria-hidden="true"></span> Loading platform data…
      </div>
    `);

    await this.loadData();
    this.container.innerHTML = this.renderShell(this.renderBody());
    this.setupEventListeners();
  }

  private async loadData(): Promise<void> {
    this.error = null;
    // Leagues, organizers and analytics are independent: a failure in one must
    // not blank out the others.
    const [leagues, organizers, leads, auditLog, analytics] = await Promise.allSettled([
      apiService.getOwnerLeagues(),
      apiService.getOrganizers(),
      apiService.getLeads(),
      apiService.getAuditLog(),
      apiService.getPlatformAnalytics(),
    ]);

    if (leagues.status === "fulfilled") {
      this.leagues = leagues.value;
    } else {
      console.error("[PlatformAdmin] Failed to load leagues:", leagues.reason);
      this.error = "We couldn't load the platform leagues. Please try again.";
      this.leagues = [];
    }

    if (organizers.status === "fulfilled") {
      this.organizers = organizers.value;
    } else {
      console.error("[PlatformAdmin] Failed to load organizers:", organizers.reason);
      this.organizers = [];
    }

    if (leads.status === "fulfilled") {
      this.leads = leads.value;
    } else {
      console.error("[PlatformAdmin] Failed to load leads:", leads.reason);
      this.leads = [];
    }

    if (auditLog.status === "fulfilled") {
      this.auditLog = auditLog.value;
    } else {
      console.error("[PlatformAdmin] Failed to load audit log:", auditLog.reason);
      this.auditLog = [];
    }

    if (analytics.status === "fulfilled") {
      this.analytics = analytics.value;
    } else {
      console.error("[PlatformAdmin] Failed to load analytics:", analytics.reason);
      this.analytics = null;
    }
  }

  /** Branded page chrome with a logout affordance. */
  private renderShell(inner: string): string {
    return `
      <div class="min-h-screen bg-background-light dark:bg-background-dark daylight:bg-day-base">
        <header class="border-b border-slate-200 dark:border-slate-800 daylight:border-day-ink/10">
          <div class="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
            <div class="flex items-center gap-2">
              <div class="flex items-center justify-center size-9 bg-primary rounded-lg">
                <span class="material-symbols-outlined text-navy-800">shield_person</span>
              </div>
              <span class="text-2xl text-slate-900 dark:text-white daylight:text-day-ink"><span class="font-medium">PADEL</span><span class="font-extrabold">PULSE</span></span>
              <span class="ml-2 badge badge-primary">Platform Admin</span>
            </div>
            <div class="flex items-center gap-2">
              <a href="#account" class="btn-secondary text-sm border border-slate-400 daylight:border-day-ink-muted">
                <span class="material-symbols-outlined text-base">account_circle</span> Account
              </a>
              <button id="admin-logout-btn" class="btn-secondary text-sm border border-slate-400 daylight:border-day-ink-muted">
                <span class="material-symbols-outlined text-base">logout</span> Logout
              </button>
            </div>
          </div>
        </header>
        <main class="max-w-5xl mx-auto px-4 py-8 space-y-8">${inner}</main>
      </div>
    `;
  }

  private renderBody(): string {
    if (this.error) {
      return `
        <div class="card text-center py-16">
          <span class="material-symbols-outlined text-5xl text-error mb-3 block">error_outline</span>
          <p class="text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft">${this.error}</p>
        </div>
      `;
    }

    return `
      <div>
        <h1 class="text-3xl font-black text-slate-900 dark:text-white daylight:text-day-ink">Platform Administration</h1>
        <p class="text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft mt-1">Curate what visitors see and (soon) review platform-wide insights.</p>
      </div>

      ${this.renderInsights()}

      ${this.renderLeads()}

      ${this.renderOrganizers()}

      ${this.renderActivity()}

      ${this.renderFeaturedCard()}
    `;
  }

  /** Recent owner administrative actions (provisioning, status, resets). */
  private renderActivity(): string {
    const items =
      this.auditLog.length === 0
        ? `<li class="text-sm text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted py-3">No activity yet.</li>`
        : this.auditLog
            .map((e) => {
              const { icon, phrase } = this.describeAction(e);
              return `
                <li class="flex items-start gap-3 py-3">
                  <span class="material-symbols-outlined text-base text-slate-400 dark:text-slate-500 daylight:text-day-ink-muted mt-0.5">${icon}</span>
                  <div class="min-w-0 flex-1">
                    <p class="text-sm text-slate-900 dark:text-white daylight:text-day-ink">
                      <span class="font-semibold">${this.escape(e.actorName ?? "An owner")}</span> ${phrase}
                    </p>
                    <p class="text-xs text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">${this.relativeTime(e.createdAt)}</p>
                  </div>
                </li>`;
            })
            .join("");

    return `
      <section class="card">
        <div class="flex items-center gap-2 mb-2">
          <span class="material-symbols-outlined text-primary daylight:text-day-ink">history</span>
          <h2 class="text-xl font-bold text-slate-900 dark:text-white daylight:text-day-ink">Recent activity</h2>
        </div>
        <ul class="divide-y divide-slate-100 dark:divide-slate-800 daylight:divide-day-ink/5">${items}</ul>
      </section>
    `;
  }

  /** Map an audit action to an icon + a human sentence fragment (target escaped). */
  private describeAction(e: AuditEntry): { icon: string; phrase: string } {
    const target = e.targetEmail ? `<span class="font-semibold">${this.escape(e.targetEmail)}</span>` : "an organizer";
    switch (e.action) {
      case "organizer.created":
        return { icon: "person_add", phrase: `added ${target}` };
      case "organizer.activated":
        return { icon: "toggle_on", phrase: `activated ${target}` };
      case "organizer.deactivated":
        return { icon: "block", phrase: `deactivated ${target}` };
      case "organizer.password_reset":
        return { icon: "key", phrase: `reset the password for ${target}` };
      default:
        return { icon: "info", phrase: `${this.escape(e.action)} ${target}` };
    }
  }

  /** Compact relative time ("just now", "5m ago", "3h ago", "2d ago", or date). */
  private relativeTime(iso: string): string {
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return "";
    const diffMs = Date.now() - then;
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  }

  /**
   * The "Leads Intake" table: prospective organizers who requested access via
   * the public landing-page form. Pending leads get an "Approve & Create
   * Credentials" action that provisions a real organizer; rejected leads stay
   * listed (greyed) as a record they were triaged.
   */
  private renderLeads(): string {
    const pendingCount = this.leads.filter((l) => l.status === "pending").length;

    const rows =
      this.leads.length === 0
        ? `<tr><td colspan="6" class="py-8 text-center text-sm text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">No access requests yet. New submissions from the landing page will appear here.</td></tr>`
        : this.leads.map((l) => this.renderLeadRow(l)).join("");

    return `
      <section class="card">
        <div class="flex items-center justify-between gap-3 mb-4">
          <div class="flex items-center gap-2">
            <span class="material-symbols-outlined text-primary daylight:text-day-ink">inbox</span>
            <h2 class="text-xl font-bold text-slate-900 dark:text-white daylight:text-day-ink">Leads Intake</h2>
            ${
              pendingCount > 0
                ? `<span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-warning/10 text-warning">${pendingCount} pending</span>`
                : ""
            }
          </div>
        </div>
        <div class="overflow-x-auto">
          <table class="w-full text-left text-sm">
            <thead>
              <tr class="text-xs uppercase text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted border-b border-slate-200 dark:border-slate-800 daylight:border-day-ink/10">
                <th class="py-3 pr-4 font-bold">Name</th>
                <th class="py-3 pr-4 font-bold">Organization</th>
                <th class="py-3 pr-4 font-bold">Email</th>
                <th class="py-3 pr-4 font-bold">Phone</th>
                <th class="py-3 pr-4 font-bold">Status</th>
                <th class="py-3 pr-4 font-bold text-right">Actions</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100 dark:divide-slate-800 daylight:divide-day-ink/5">
              ${rows}
            </tbody>
          </table>
        </div>
      </section>
    `;
  }

  private renderLeadRow(l: OrganizerLead): string {
    const isRejected = l.status === "rejected";

    const statusBadge = isRejected
      ? `<span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300 daylight:bg-day-sunken daylight:text-day-ink-muted"><span class="material-symbols-outlined text-sm">block</span>Rejected</span>`
      : `<span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-warning/10 text-warning"><span class="material-symbols-outlined text-sm">hourglass_top</span>Pending</span>`;

    // Pending leads can be approved (provision an organizer) or rejected.
    // Rejected leads carry no actions — the decision is already recorded.
    const actions = isRejected
      ? `<span class="text-xs text-slate-400 dark:text-slate-500 daylight:text-day-ink-muted">—</span>`
      : `
        <div class="flex items-center justify-end gap-1">
          <button data-approve-lead="${l.id}" class="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-bold text-success hover:bg-success/10 transition" title="Approve & create credentials">
            <span class="material-symbols-outlined text-base">person_add</span>Approve &amp; Create Credentials
          </button>
          <button data-reject-lead="${l.id}" class="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-bold text-error hover:bg-error/10 transition" title="Reject lead">
            <span class="material-symbols-outlined text-base">block</span>Reject
          </button>
        </div>`;

    const rowClass = isRejected ? "opacity-60" : "";

    return `
      <tr class="text-slate-900 dark:text-white daylight:text-day-ink ${rowClass}">
        <td class="py-3 pr-4 font-semibold">${this.escape(l.name)}</td>
        <td class="py-3 pr-4 text-slate-600 dark:text-slate-300 daylight:text-day-ink-soft">${this.escape(l.organizationName)}</td>
        <td class="py-3 pr-4 text-slate-600 dark:text-slate-300 daylight:text-day-ink-soft">${this.escape(l.email)}</td>
        <td class="py-3 pr-4 text-slate-600 dark:text-slate-300 daylight:text-day-ink-soft whitespace-nowrap">${this.escape(l.phone)}</td>
        <td class="py-3 pr-4 whitespace-nowrap">${statusBadge}</td>
        <td class="py-3 pr-4">${actions}</td>
      </tr>
    `;
  }

  /**
   * The Organizers table: every organizer account with status and per-row
   * actions (toggle active, reset password) plus the onboarding entry point.
   */
  private renderOrganizers(): string {
    const rows =
      this.organizers.length === 0
        ? `<tr><td colspan="5" class="py-8 text-center text-sm text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">No organizers yet. Add your first one to get started.</td></tr>`
        : this.organizers.map((o) => this.renderOrganizerRow(o)).join("");

    return `
      <section class="card">
        <div class="flex items-center justify-between gap-3 mb-4">
          <div class="flex items-center gap-2">
            <span class="material-symbols-outlined text-primary daylight:text-day-ink">badge</span>
            <h2 class="text-xl font-bold text-slate-900 dark:text-white daylight:text-day-ink">Organizers</h2>
          </div>
          <button id="add-organizer-btn" class="btn-primary text-sm">
            <span class="material-symbols-outlined text-base">person_add</span>
            <span>Add Organizer</span>
          </button>
        </div>
        <div class="overflow-x-auto">
          <table class="w-full text-left text-sm">
            <thead>
              <tr class="text-xs uppercase text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted border-b border-slate-200 dark:border-slate-800 daylight:border-day-ink/10">
                <th class="py-3 pr-4 font-bold">Name</th>
                <th class="py-3 pr-4 font-bold">Email</th>
                <th class="py-3 pr-4 font-bold">Status</th>
                <th class="py-3 pr-4 font-bold">Created</th>
                <th class="py-3 pr-4 font-bold text-right">Actions</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100 dark:divide-slate-800 daylight:divide-day-ink/5">
              ${rows}
            </tbody>
          </table>
        </div>
      </section>
    `;
  }

  private renderOrganizerRow(o: Organizer): string {
    const created = new Date(o.createdAt).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

    const statusBadge = o.active
      ? `<span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-success/10 text-success"><span class="material-symbols-outlined text-sm">check_circle</span>Active</span>`
      : `<span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300 daylight:bg-day-sunken daylight:text-day-ink-muted"><span class="material-symbols-outlined text-sm">block</span>Inactive</span>`;

    const pendingReset = o.mustChangePassword
      ? `<span class="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-warning/10 text-warning" title="Awaiting first-login password change"><span class="material-symbols-outlined text-sm">hourglass_top</span>Pending reset</span>`
      : "";

    const toggleLabel = o.active ? "Deactivate" : "Activate";
    const toggleIcon = o.active ? "toggle_off" : "toggle_on";

    return `
      <tr class="text-slate-900 dark:text-white daylight:text-day-ink">
        <td class="py-3 pr-4 font-semibold">${this.escape(o.name)}</td>
        <td class="py-3 pr-4 text-slate-600 dark:text-slate-300 daylight:text-day-ink-soft">${this.escape(o.email)}</td>
        <td class="py-3 pr-4 whitespace-nowrap">${statusBadge}${pendingReset}</td>
        <td class="py-3 pr-4 text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted whitespace-nowrap">${created}</td>
        <td class="py-3 pr-4">
          <div class="flex items-center justify-end gap-1">
            <button data-toggle-id="${o.id}" data-active="${o.active}" class="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-bold text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 daylight:text-day-ink-soft daylight:hover:bg-day-sunken transition" title="${toggleLabel}">
              <span class="material-symbols-outlined text-base">${toggleIcon}</span>${toggleLabel}
            </button>
            <button data-reset-id="${o.id}" class="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-bold text-primary hover:bg-primary/10 daylight:text-day-ink transition" title="Reset password">
              <span class="material-symbols-outlined text-base">key</span>Reset
            </button>
          </div>
        </td>
      </tr>
    `;
  }

  /** The four core platform metrics + when organizers are reporting results. */
  private renderInsights(): string {
    const a = this.analytics;

    const heading = `
      <div class="flex items-center gap-2">
        <span class="material-symbols-outlined text-primary daylight:text-day-ink">insights</span>
        <h2 class="text-xl font-bold text-slate-900 dark:text-white daylight:text-day-ink">Reports &amp; Insights</h2>
      </div>
    `;

    if (!a) {
      return `
        <div class="card">
          ${heading}
          <p class="mt-3 text-sm text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft">We couldn't load platform insights right now. Please try again.</p>
        </div>
      `;
    }

    return `
      <section class="space-y-4">
        ${heading}
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          ${this.metricCard("trophy", "Active Leagues", `${a.totalActiveLeagues}`, "Across every organization")}
          ${this.metricCard("groups", "Active Players", `${a.uniqueActivePlayers}`, "Unique, platform-wide")}
          ${this.metricCard("favorite", "Organizer Retention", `${a.organizerRetentionRate}%`, "Tenants running a live league")}
          ${this.metricCard("task_alt", "Match Completion", `${a.matchCompletionRate}%`, "Reported vs. scheduled")}
        </div>
        ${renderActivityTrendCard(a.activityTrend)}
        ${this.renderReportingActivity(a.reportingActivity)}
      </section>
    `;
  }

  private metricCard(icon: string, label: string, value: string, sub: string): string {
    return `
      <div class="bg-white dark:bg-slate-800 daylight:bg-day-card p-4 rounded-lg shadow daylight:shadow-day-sm border border-slate-200 dark:border-slate-700 daylight:border-day-ink/10">
        <div class="flex items-center gap-3 mb-2">
          <div class="p-2 bg-primary/10 rounded-lg text-primary daylight:text-day-ink">
            <span class="material-symbols-outlined">${icon}</span>
          </div>
          <h3 class="text-sm font-medium text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">${this.escape(label)}</h3>
        </div>
        <p class="text-3xl font-black text-slate-900 dark:text-white daylight:text-day-ink">${this.escape(value)}</p>
        <small class="text-xs text-slate-400 dark:text-slate-500 daylight:text-day-ink-muted">${this.escape(sub)}</small>
      </div>
    `;
  }

  /**
   * When organizers report results, by weekday. A platform-owner signal: it
   * shows when organizers are active in the system (support/comms timing,
   * load planning) rather than anything about the leagues themselves.
   */
  private renderReportingActivity(activity: WeekdayActivity[]): string {
    const labels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const max = Math.max(1, ...activity.map((d) => d.count));
    const total = activity.reduce((sum, d) => sum + d.count, 0);

    const bars = activity
      .map((d) => {
        const heightPct = Math.round((d.count / max) * 100);
        return `
          <div class="flex flex-col items-center justify-end gap-2 flex-1">
            <span class="text-xs font-mono text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">${d.count}</span>
            <div class="w-full flex items-end" style="height: 96px">
              <div class="w-full rounded-t bg-primary daylight:shadow-day-ball transition-all" style="height: ${Math.max(heightPct, d.count > 0 ? 6 : 0)}%" title="${d.count} reported"></div>
            </div>
            <span class="text-xs text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">${labels[d.weekday] ?? ""}</span>
          </div>
        `;
      })
      .join("");

    return `
      <div class="card">
        <div class="flex items-baseline justify-between mb-4">
          <h3 class="font-bold text-slate-900 dark:text-white daylight:text-day-ink">Organizer Reporting Activity</h3>
          <span class="text-xs text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">When results get entered, by weekday</span>
        </div>
        ${
          total === 0
            ? `<p class="text-sm text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted py-6 text-center">No results have been reported yet.</p>`
            : `<div class="flex items-end gap-3">${bars}</div>`
        }
      </div>
    `;
  }

  private renderFeaturedCard(): string {
    const featured = this.leagues.filter((l) => l.isFeatured);

    if (this.leagues.length === 0) {
      return `
        <div class="card">
          <h2 class="text-xl font-bold text-slate-900 dark:text-white daylight:text-day-ink mb-2">Featured League</h2>
          <p class="text-sm text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft">There are no active leagues to feature yet.</p>
        </div>
      `;
    }

    const options = this.leagues
      .map(
        (l) =>
          `<option value="${l.id}"${l.isFeatured ? " selected" : ""}>${this.escape(l.name)} — ${this.escape(
            l.tenantName,
          )} (${l.playerCount} players)</option>`,
      )
      .join("");

    const currentlyFeatured = featured.length
      ? `
        <div class="mt-6 pt-6 border-t border-slate-200 dark:border-slate-800 daylight:border-day-ink/10">
          <p class="text-xs font-bold uppercase text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted mb-3">Currently featured</p>
          <ul class="space-y-2">
            ${featured
              .map(
                (l) => `
              <li class="flex items-center justify-between bg-slate-50 dark:bg-slate-800/50 daylight:bg-day-sunken rounded-lg px-4 py-3">
                <div>
                  <p class="font-bold text-slate-900 dark:text-white daylight:text-day-ink">${this.escape(l.name)}</p>
                  <p class="text-xs text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">${this.escape(l.tenantName)}</p>
                </div>
                <button data-unfeature-id="${l.id}" class="text-sm font-bold text-error hover:underline flex items-center gap-1">
                  <span class="material-symbols-outlined text-base">close</span> Remove
                </button>
              </li>`,
              )
              .join("")}
          </ul>
        </div>`
      : `<p class="mt-4 text-sm text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">No league is currently featured.</p>`;

    return `
      <div class="card">
        <h2 class="text-xl font-bold text-slate-900 dark:text-white daylight:text-day-ink mb-1">Featured League</h2>
        <p class="text-sm text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft mb-4">Highlight one league per organization on the public landing page.</p>
        <div class="flex flex-col sm:flex-row gap-3">
          <select id="featured-select" class="flex-1 px-4 py-3 border border-slate-300 dark:border-slate-700 daylight:border-day-ink/20 bg-white dark:bg-slate-800 daylight:bg-day-raised rounded-lg text-slate-900 dark:text-white daylight:text-day-ink focus:ring-2 focus:ring-primary focus:border-transparent transition">
            ${options}
          </select>
          <button id="feature-btn" class="btn-primary">
            <span class="material-symbols-outlined">star</span>
            <span>Set as Featured</span>
          </button>
        </div>
        ${currentlyFeatured}
      </div>
    `;
  }

  private setupEventListeners(): void {
    const logoutBtn = this.container.querySelector("#admin-logout-btn");
    logoutBtn?.addEventListener("click", () => {
      localStorage.removeItem("authToken");
      localStorage.removeItem("userRole");
      window.location.hash = "#login";
    });

    const featureBtn = this.container.querySelector("#feature-btn");
    featureBtn?.addEventListener("click", async () => {
      const select = this.container.querySelector("#featured-select") as HTMLSelectElement | null;
      const id = select ? parseInt(select.value, 10) : NaN;
      if (Number.isNaN(id)) return;
      await this.runAction(() => apiService.featureLeague(id));
    });

    this.container.querySelectorAll("[data-unfeature-id]").forEach((btn) => {
      btn.addEventListener("click", async () => {
        const id = parseInt((btn as HTMLElement).dataset.unfeatureId || "", 10);
        if (Number.isNaN(id)) return;
        await this.runAction(() => apiService.unfeatureLeague(id));
      });
    });

    // Organizer onboarding + management.
    this.container.querySelector("#add-organizer-btn")?.addEventListener("click", () => {
      this.openOnboardingModal();
    });

    this.container.querySelectorAll("[data-toggle-id]").forEach((btn) => {
      btn.addEventListener("click", async () => {
        const el = btn as HTMLElement;
        const id = parseInt(el.dataset.toggleId || "", 10);
        if (Number.isNaN(id)) return;
        // data-active reflects the CURRENT state; we flip it.
        const nextActive = el.dataset.active !== "true";
        // Deactivating instantly revokes the organizer's live sessions, so make
        // it a deliberate action via the styled confirmation modal.
        if (!nextActive) {
          const confirmed = await new ConfirmationModal().open(
            "Deactivate organizer",
            "They'll be logged out immediately and won't be able to sign in until you reactivate them.",
            "Deactivate",
            true,
          );
          if (!confirmed) return;
        }
        try {
          await apiService.setOrganizerStatus(id, nextActive);
          showNotification(nextActive ? "Organizer activated." : "Organizer deactivated.", "success");
        } catch (error) {
          console.error("[PlatformAdmin] Toggle status failed:", error);
          showNotification(error instanceof Error ? error.message : "Failed to update status.", "error");
        }
        await this.render();
      });
    });

    this.container.querySelectorAll("[data-reset-id]").forEach((btn) => {
      btn.addEventListener("click", async () => {
        const id = parseInt((btn as HTMLElement).dataset.resetId || "", 10);
        if (Number.isNaN(id)) return;
        const confirmed = await new ConfirmationModal().open(
          "Reset password",
          "Generate a new temporary password for this organizer? Their current password will stop working, and they'll be prompted to set a new one at next login.",
          "Reset password",
          true,
        );
        if (!confirmed) return;
        try {
          const creds = await apiService.resetOrganizerPassword(id);
          this.showCredentialsModal(creds, { isReset: true });
          await this.render();
        } catch (error) {
          console.error("[PlatformAdmin] Reset password failed:", error);
          showNotification(error instanceof Error ? error.message : "Failed to reset password.", "error");
        }
      });
    });

    // Lead intake: approve (provision an organizer) or reject a pending lead.
    this.container.querySelectorAll("[data-approve-lead]").forEach((btn) => {
      btn.addEventListener("click", async () => {
        const id = parseInt((btn as HTMLElement).dataset.approveLead || "", 10);
        if (Number.isNaN(id)) return;
        const confirmed = await new ConfirmationModal().open(
          "Approve & create credentials",
          "This creates a live organizer account from the lead and generates a one-time temporary password for you to send. Continue?",
          "Approve",
          false,
        );
        if (!confirmed) return;
        try {
          const creds = await apiService.approveLead(id);
          this.showLeadCredentialsModal(creds);
          // Refresh so the lead leaves the intake table and the new organizer appears.
          await this.render();
        } catch (error) {
          console.error("[PlatformAdmin] Approve lead failed:", error);
          showNotification(error instanceof Error ? error.message : "Failed to approve lead.", "error");
        }
      });
    });

    this.container.querySelectorAll("[data-reject-lead]").forEach((btn) => {
      btn.addEventListener("click", async () => {
        const id = parseInt((btn as HTMLElement).dataset.rejectLead || "", 10);
        if (Number.isNaN(id)) return;
        const confirmed = await new ConfirmationModal().open(
          "Reject lead",
          "Mark this access request as rejected? It will stay listed for your records but can't be approved afterwards.",
          "Reject",
          true,
        );
        if (!confirmed) return;
        try {
          await apiService.rejectLead(id);
          showNotification("Lead rejected.", "success");
          await this.render();
        } catch (error) {
          console.error("[PlatformAdmin] Reject lead failed:", error);
          showNotification(error instanceof Error ? error.message : "Failed to reject lead.", "error");
        }
      });
    });
  }

  /** The onboarding form modal: collect Name + Email, then provision. */
  private openOnboardingModal(): void {
    const modal = document.createElement("div");
    modal.className =
      "fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm";
    modal.innerHTML = `
      <div class="relative w-full max-w-md bg-white dark:bg-card-dark daylight:bg-day-card rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 daylight:border-day-ink/10 p-8">
        <button id="onboard-close" class="absolute top-4 right-4 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
          <span class="material-symbols-outlined">close</span>
        </button>
        <h2 class="text-2xl font-black text-slate-900 dark:text-white daylight:text-day-ink mb-1">Add Organizer</h2>
        <p class="text-sm text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft mb-6">A temporary password is generated for you to copy and send.</p>
        <form id="onboard-form" class="space-y-4">
          <div>
            <label class="block text-sm font-semibold text-slate-900 dark:text-white daylight:text-day-ink mb-2">Name</label>
            <input type="text" id="onboard-name" required class="w-full px-4 py-3 border border-slate-300 dark:border-slate-700 daylight:border-day-ink/20 bg-white dark:bg-slate-800 daylight:bg-day-raised rounded-lg text-slate-900 dark:text-white daylight:text-day-ink focus:ring-2 focus:ring-primary focus:border-transparent transition" placeholder="Club or organizer name" />
          </div>
          <div>
            <label class="block text-sm font-semibold text-slate-900 dark:text-white daylight:text-day-ink mb-2">Email</label>
            <input type="email" id="onboard-email" required class="w-full px-4 py-3 border border-slate-300 dark:border-slate-700 daylight:border-day-ink/20 bg-white dark:bg-slate-800 daylight:bg-day-raised rounded-lg text-slate-900 dark:text-white daylight:text-day-ink focus:ring-2 focus:ring-primary focus:border-transparent transition" placeholder="organizer@example.com" />
          </div>
          <div id="onboard-error" class="hidden bg-error/10 border border-error/30 text-error rounded-lg p-3 text-sm font-medium"></div>
          <button type="submit" id="onboard-submit" class="btn-primary w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed">
            <span class="material-symbols-outlined">person_add</span><span>Create Organizer</span>
          </button>
        </form>
      </div>
    `;

    const close = () => modal.remove();
    modal.querySelector("#onboard-close")?.addEventListener("click", close);
    modal.addEventListener("click", (e) => {
      if (e.target === modal) close();
    });

    const form = modal.querySelector("#onboard-form") as HTMLFormElement;
    const errorDiv = modal.querySelector("#onboard-error") as HTMLElement;
    const submitBtn = modal.querySelector("#onboard-submit") as HTMLButtonElement;

    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      errorDiv.classList.add("hidden");
      const name = (modal.querySelector("#onboard-name") as HTMLInputElement).value.trim();
      const email = (modal.querySelector("#onboard-email") as HTMLInputElement).value.trim();

      submitBtn.disabled = true;
      try {
        const creds = await apiService.createOrganizer({ name, email });
        close();
        this.showCredentialsModal(creds, { isReset: false });
        // Refresh the table so the new organizer appears.
        await this.render();
      } catch (error) {
        console.error("[PlatformAdmin] Create organizer failed:", error);
        errorDiv.textContent = error instanceof Error ? error.message : "Failed to create organizer.";
        errorDiv.classList.remove("hidden");
        submitBtn.disabled = false;
      }
    });

    document.body.appendChild(modal);
    (modal.querySelector("#onboard-name") as HTMLInputElement)?.focus();
  }

  /**
   * Success modal showing the one-time credentials with a Copy button. The
   * temporary password is shown only here (the server never returns it again).
   */
  private showCredentialsModal(creds: OrganizerCredentials, opts: { isReset: boolean }): void {
    const text = this.buildCredentialsText(creds.email, creds.temporaryPassword, opts.isReset);
    const title = opts.isReset ? "Password reset" : "Organizer created";
    this.presentCredentials(title, text);
  }

  /**
   * Success modal for an APPROVED lead. Uses the personalized welcome template
   * (the lead carries a real contact name) and the same copy-to-clipboard modal
   * as manual onboarding.
   */
  private showLeadCredentialsModal(creds: LeadCredentials): void {
    const text = this.buildLeadCredentialsText(creds.name, creds.email, creds.temporaryPassword);
    this.presentCredentials("Organizer approved", text);
  }

  /** The chat-ready welcome message for a lead approved into an organizer. */
  private buildLeadCredentialsText(name: string, email: string, temporaryPassword: string): string {
    return `Hello ${name}! Welcome to PadelPulse.
Your workspace is ready. You can log in using these temporary credentials:
Email: ${email}
Temporary Password: ${temporaryPassword}
(You will be prompted to update your password on your first login)`;
  }

  /** Shared credentials modal: a read-only text block + a Copy button. */
  private presentCredentials(title: string, text: string): void {
    const modal = document.createElement("div");
    modal.className =
      "fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm";
    modal.innerHTML = `
      <div class="relative w-full max-w-md bg-white dark:bg-card-dark daylight:bg-day-card rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 daylight:border-day-ink/10 p-8">
        <div class="text-center mb-5">
          <div class="flex items-center justify-center size-12 bg-success/15 text-success rounded-full mx-auto mb-3">
            <span class="material-symbols-outlined text-3xl">check_circle</span>
          </div>
          <h2 class="text-xl font-black text-slate-900 dark:text-white daylight:text-day-ink">${title}</h2>
          <p class="text-sm text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft mt-1">Copy these credentials and send them to the organizer. The temporary password won't be shown again.</p>
        </div>
        <pre id="creds-text" class="whitespace-pre-wrap text-sm bg-slate-50 dark:bg-slate-800/50 daylight:bg-day-sunken border border-slate-200 dark:border-slate-700 daylight:border-day-ink/10 rounded-lg p-4 text-slate-800 dark:text-white daylight:text-day-ink font-mono">${this.escape(text)}</pre>
        <p class="mt-3 flex items-start gap-1.5 text-xs text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">
          <span class="material-symbols-outlined text-sm flex-shrink-0">info</span>
          <span>For security, this password isn't stored and can't be shown again. If you lose it before sending, use <span class="font-semibold">Reset</span> on the organizer's row to generate a new one.</span>
        </p>
        <div class="mt-5 flex gap-3">
          <button id="creds-copy" class="btn-primary flex-1 justify-center">
            <span class="material-symbols-outlined">content_copy</span><span id="creds-copy-label">Copy Credentials</span>
          </button>
          <button id="creds-done" class="btn-secondary border border-slate-300 dark:border-slate-700 daylight:border-day-ink-muted">Done</button>
        </div>
      </div>
    `;

    const close = () => modal.remove();
    modal.querySelector("#creds-done")?.addEventListener("click", close);
    modal.addEventListener("click", (e) => {
      if (e.target === modal) close();
    });

    const copyBtn = modal.querySelector("#creds-copy") as HTMLButtonElement;
    const copyLabel = modal.querySelector("#creds-copy-label") as HTMLElement;
    copyBtn?.addEventListener("click", async () => {
      const ok = await this.copyToClipboard(text);
      copyLabel.textContent = ok ? "Copied!" : "Press Ctrl+C to copy";
      if (ok) setTimeout(() => (copyLabel.textContent = "Copy Credentials"), 2000);
    });

    document.body.appendChild(modal);
  }

  /** The chat-ready welcome message template. */
  private buildCredentialsText(email: string, temporaryPassword: string, isReset: boolean): string {
    const appUrl = window.location.origin;
    const intro = isReset
      ? "🎾 Your PadelPulse password has been reset.\nHere are your new login details."
      : "🎾 Welcome to PadelPulse!\nYour organizer account is ready.";
    return `${intro}

Login URL: ${appUrl}
Username: ${email}
Temporary Password: ${temporaryPassword}

*Note: You will be prompted to choose a permanent password on your first login.*`;
  }

  /**
   * Copy text to the clipboard, falling back to the legacy execCommand path for
   * non-secure contexts where navigator.clipboard is unavailable.
   */
  private async copyToClipboard(text: string): Promise<boolean> {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch {
      /* fall through to the legacy path */
    }
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      ta.remove();
      return ok;
    } catch {
      return false;
    }
  }

  /** Run a mutation, then re-render from fresh server state. */
  private async runAction(action: () => Promise<unknown>): Promise<void> {
    try {
      await action();
    } catch (error) {
      console.error("[PlatformAdmin] Action failed:", error);
    }
    await this.render();
  }

  private escape(value: string): string {
    const div = document.createElement("div");
    div.textContent = value ?? "";
    return div.innerHTML;
  }
}
